import { forwardRef, useCallback, useEffect } from 'react';

import debounce from 'lodash/debounce';

import { getCards } from 'api/Cards';

import { iaRa } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import NewCard from '../../PayWithCardModal/NewCard';

import SavedCards from './SavedCards';

const SCROLL_THRESHOLD = 30;

const CardContent = forwardRef(({ active, setActive, patientId, setErrorMessage }, ref) => {
  const { values, terminalId, processor } = usePaymentContext();

  const {
    data: cards,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteFetchData({
    queryKey: 'cards',
    queryFn: (navigate, params) => getCards(navigate, params),
    formatData: (data) => iaRa(data).filter((card) => !!card?.id),
    params: { patientId, terminalId, withCount: true },
    dependencies: [terminalId, patientId],
    options: {
      enabled: !!terminalId && !!patientId
    }
  });

  const handleScroll = useCallback(() => {
    const div = ref.current;
    if (!div) return;

    const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const div = ref.current;
    if (!div) return;

    const debounced = debounce(handleScroll, 200);
    div.addEventListener('scroll', debounced);

    return () => {
      div.removeEventListener('scroll', debounced);
      debounced.cancel();
    };
  }, [handleScroll, active]);

  return (
    <>
      {active === 'saved-cards' && (
        <SavedCards cards={cards} active={values.methodDetail} setActive={setActive} />
      )}
      {active === 'credit-card' && processor === 'payroc' && (
        <NewCard onError={(error) => setErrorMessage(error)} hideButton />
      )}
    </>
  );
});

export default CardContent;
